import { useState, useMemo } from "react";
import { SYSTEM_COLORS } from "../../../../components/system.colors"
import { createPercentFormatter } from '../../../../utils/formatter/formatter'

const LEVEL_KEYS = ["level1", "level2", "level3", "level4", "level5", "level6", "level7", "level8", "level9", "level10"];

const getLevelChain = (trend) => {
  const filled = LEVEL_KEYS
    .map((key) => trend[key])
    .filter((value) => value != null && value !== "");

  if (filled.length === 0) return { name: `Produto ${trend.productId}`, category: "" };

  const name = filled[filled.length - 1];
  const category = filled.slice(0, -1).join(" - ");

  return { name, category };
};

const Switch = ({ ctx, actions }) => {
  const { checked } = ctx || {};
  const { onChange } = actions || {};
  return (
    <span
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      style={{
        width: 34, height: 18, borderRadius: 10, flexShrink: 0, cursor: "pointer",
        background: checked ? SYSTEM_COLORS.RED.BRICK_RED : SYSTEM_COLORS.GRAY.GAINSBORO, 
        position: "relative", transition: "background 0.15s",
      }}
    >
      <span style={{
        position: "absolute", top: 2, left: checked ? 18 : 2, width: 14, height: 14,
        borderRadius: "50%", background: SYSTEM_COLORS.WHITE.WHITE, transition: "left 0.15s",
        boxShadow: `0 1px 2px ${SYSTEM_COLORS.BLACK.BLACK_25_PERCENT}`
      }} />
    </span>
  );
};

export function TrendChangesPanel({ ctx, actions }) {
  
  const { hierarchy, trends = [], activeOppsWithTypes = [] } = ctx || {};
  const { onFocusProduct } = actions || {};
  
  const [confirmedOnly, setConfirmedOnly] = useState(false);

  const levels = hierarchy?.levels ?? [];
  const labels = hierarchy?.labels ?? {};

  const activeProductIds = useMemo(
    () => new Set(activeOppsWithTypes.map((o) => o.productId)),
    [activeOppsWithTypes]
  );

  const changes = useMemo(
    () =>
      trends.map((t) => {
        const deltaPct = Number(t.percentForecast);
        const { name, category } = getLevelChain(t);

        return {
          key: t.salesTrendsId,
          productId: t.productId,
          product: name,
          category,
          direction: deltaPct >= 0 ? "up" : "down",
          deltaPct: createPercentFormatter({ minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(deltaPct),
          note: t.salesTrendText,
          confirmed: !!t.trendConfirmed,
          hasAction: activeProductIds.has(t.productId),
        };
      }),
    [trends, activeProductIds]
  );

  const visible = confirmedOnly
    ? changes.filter((c) => c.confirmed)
    : changes;

  return (
    <section
      style={{
        border: `1px solid ${SYSTEM_COLORS.GRAY.LIGHT_GRAY}`, borderRadius: "12px", 
        boxShadow: `0 1px 3px ${SYSTEM_COLORS.BLACK.BLACK_4_PERCENT}`,
        padding: "20px 24px", display: "flex", flexDirection: "column", 
        backgroundColor: SYSTEM_COLORS.WHITE.WHITE,
        gap: 14, flex: 1, minHeight: 0, height: "100%" }}>

      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
        <hgroup>
          <p style={{ fontSize: "0.938rem", fontWeight: 700, color: SYSTEM_COLORS.BLACK.EERIE_BLACK }}>
            Mudanças de Tendência de Vendas
          </p>

          <p style={{ fontSize: "0.75rem", color: SYSTEM_COLORS.GRAY.GREY, marginTop: 2 }}>
            Se a venda seguir neste ritmo, a "nova tendência" tende a substituir o forecast
          </p>          
        </hgroup>

        {/* Badge IA */}
        <svg
          width="55" height="25" viewBox="0 0 55 25" fill="none" xmlns="http://www.w3.org/2000/svg"
          style={{ flexShrink: 0, display: "block" }}>
          <rect x="0" y="0" width="55" height="25" rx="12.5" fill={SYSTEM_COLORS.RED.BRICK_RED} />

          <g  
            transform="translate(6.5 4.5) scale(0.67)" stroke={SYSTEM_COLORS.WHITE.WHITE}
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M11.017 2.814a1 1 0 0 1 1.966 0l1.051 5.558a2 2 0 0 0 1.594 1.594l5.558 1.051a1 1 0 0 1 0 1.966l-5.558 1.051a2 2 0 0 0-1.594 1.594l-1.051 5.558a1 1 0 0 1-1.966 0l-1.051-5.558a2 2 0 0 0-1.594-1.594l-5.558-1.051a1 1 0 0 1 0-1.966l5.558-1.051a2 2 0 0 0 1.594-1.594z" />
            <path d="M20 2v4" />
            <path d="M22 4h-4" />
            <circle cx="4" cy="20" r="2" />
          </g>

          <text
            x="27" y="16" fill={SYSTEM_COLORS.WHITE.WHITE}
            fontFamily="Arial, sans-serif" fontSize="11" fontWeight="700">
            IA
          </text>
        </svg>
      </div>

      <label
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between", 
          padding: "10px 14px", background: SYSTEM_COLORS.WHITE.WHITE_SMOKE, border: `1px solid ${SYSTEM_COLORS.GRAY.PLATINUM}`, 
          borderRadius: 18, cursor: "pointer", marginBottom: 4 }}>
        <span
          style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "0.813rem", color: SYSTEM_COLORS.BLACK.JET }}>
          <Switch
            ctx={{ checked: confirmedOnly }}
            actions={{ onChange: setConfirmedOnly }}/>
          Tendência confirmada
        </span>

        <span style={{ fontSize: "0.75rem", color: SYSTEM_COLORS.GRAY.GREY }}>
          {visible.length} itens
        </span>
      </label>

      <div
        style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflowY: "auto" }}>
        {visible.map((change, i) => {
          const isUp = change.direction === "up";
          const trendColor = isUp ? SYSTEM_COLORS.GREEN.FOREST_GREEN : SYSTEM_COLORS.RED.BRICK_RED;

          return (
            <div
              key={change.key}
              style={{ 
                  display: "flex", gap: 10, padding: "12px 0", 
                  borderTop: i === 0 ? "none" : `1px solid ${SYSTEM_COLORS.GRAY.CULTURED}` }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", marginTop: 6, flexShrink: 0, background: trendColor }}/>

              <div
                style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
                <span style={{ fontSize: "0.844rem", fontWeight: 700, color: SYSTEM_COLORS.BLACK.EERIE_BLACK }}>
                  {change.product}
                </span>

                {change.category && (
                  <span style={{ fontSize: "0.75rem", color: SYSTEM_COLORS.GRAY.GREY }}>
                    {change.category}
                  </span>
                )}

                <span
                  style={{ display: "flex", alignItems: "center", gap: 4, fontSize: "0.781rem", fontWeight: 700, color: trendColor }}>
                  {isUp ? (
                    <svg 
                      width={12} height={12} viewBox="0 0 24 24" fill="none" stroke={SYSTEM_COLORS.GREEN.FOREST_GREEN} 
                      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M16 7h6v6" />
                      <path d="m22 7-8.5 8.5-5-5L2 17" />
                    </svg>
                  ) : (
                    <svg 
                      width={12} height={12} viewBox="0 0 24 24" fill="none" stroke={SYSTEM_COLORS.RED.BRICK_RED} 
                      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M16 17h6v-6" />
                      <path d="m22 17-8.5-8.5-5 5L2 7" />
                    </svg> 
                  )}

                  {change.deltaPct > 0 ? "+" : ""}
                  {change.deltaPct} sobre o forecast
                </span>

                {change.note && (
                  <span style={{ fontSize: "0.75rem", color: SYSTEM_COLORS.GRAY.MEDIUM_GRAY }}>
                    {change.note}
                  </span>
                )}

                {change.hasAction && (
                  <button
                    onClick={() => onFocusProduct?.(change.productId)}
                    className="oi-outline-btn"
                    style={{
                      alignSelf: "flex-start", marginTop: 4, display: "flex",
                      alignItems: "center", gap: 4, border: `1px solid ${SYSTEM_COLORS.ORANGE.BURNT_ORANGE_40}`, borderRadius: 10,
                      backgroundColor: "#f7f7f7", boxShadow: "0 1px 2px rgba(0,0,0,0.06)", color: SYSTEM_COLORS.ORANGE.BURNT_ORANGE,
                      fontSize: "0.688rem", fontWeight: 700, padding: "5px 12px", cursor: "pointer",
                    }}>
                    Ações
                    <svg 
                      width={12} height={12} viewBox="0 0 24 24" fill="none" stroke={SYSTEM_COLORS.ORANGE.BURNT_ORANGE} 
                      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}